export const PARTY_PASS_PRODUCT_KEY = "party_pass";
export const STRIPE_MIN_UNIT_AMOUNT_CENTS = 50;
export const DEFAULT_BILLING_CURRENCY = "usd";

/**
 * Party Pass price quoted in static marketing copy (catalogs, show pages,
 * JSON-LD). Checkout charges the admin-configured price in billing settings,
 * so change both together.
 */
export const PARTY_PASS_MARKETING_PRICE_CENTS = 999;

// Stripe Managed Payments requires an eligible digital product tax code.
// Party Pass is hosted consumer software, not a download:
// https://docs.stripe.com/payments/managed-payments/eligibility#product-tax-code-requirements
export const PARTY_PASS_STRIPE_TAX_CODE = "txcd_10103000";

export function parseDollarsToCents(input: string): number | null {
  const trimmed = input.trim().replace(/^\$/, "").replace(/,/g, "");
  if (!trimmed) {
    return null;
  }

  if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) {
    return null;
  }

  const [dollars = "0", centsPart = ""] = trimmed.split(".");
  const cents = Number.parseInt(dollars, 10) * 100 + Number.parseInt(centsPart.padEnd(2, "0") || "0", 10);
  if (!Number.isFinite(cents) || cents < 0) {
    return null;
  }

  return cents;
}

export function formatPriceLabel(
  unitAmountCents: number,
  currency: string = DEFAULT_BILLING_CURRENCY,
): string {
  if (!Number.isFinite(unitAmountCents) || unitAmountCents < 0) {
    return "";
  }

  const normalizedCurrency = currency.trim().toUpperCase() || "USD";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: normalizedCurrency,
    minimumFractionDigits: unitAmountCents % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(unitAmountCents / 100);
}

export function centsToDollarInput(unitAmountCents: number): string {
  if (!Number.isFinite(unitAmountCents) || unitAmountCents < 0) {
    return "0.00";
  }

  return (unitAmountCents / 100).toFixed(2);
}

export function stripePriceMatchesLocal(
  stripeUnitAmount: number | null | undefined,
  localUnitAmountCents: number,
): boolean {
  return stripeUnitAmount === localUnitAmountCents;
}

export function shouldReplaceStripePrice(input: {
  localUnitAmountCents: number;
  stripePriceId?: string | null;
  stripeUnitAmount?: number | null;
  stripePriceActive?: boolean | null;
}): boolean {
  if (!input.stripePriceId) {
    return true;
  }

  if (input.stripePriceActive === false) {
    return true;
  }

  return !stripePriceMatchesLocal(input.stripeUnitAmount, input.localUnitAmountCents);
}

export function parsePositiveInt(value: string | number | null | undefined): number | null {
  if (typeof value === "number") {
    if (!Number.isInteger(value) || value < 1) {
      return null;
    }
    return value;
  }

  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed || !/^\d+$/.test(trimmed)) {
    return null;
  }

  const parsed = Number.parseInt(trimmed, 10);
  if (!Number.isInteger(parsed) || parsed < 1) {
    return null;
  }

  return parsed;
}
